import React from "react";
import { UX_EVALUATION } from "../../../../types/enum";
import { InputField } from "./InputField";
import { SelectField } from "./SelectField";
import { ContentField } from "./ContentField";
import { StudyLinksField } from "./StudyLinksField";
import { ImageField } from "./ImageField";
import { DataSourceToggle } from "./DataSourceToggle";
import { Category } from "../../../../entities/category";
import { Keyword } from "../../../../entities/keyword";
import { ArticleImage } from "../../../../entities/article";

export interface ArticleFormData {
  titleKo: string;
  titleEn: string;
  contentKo: string;
  contentEn: string;
  author: {
    id: string;
    authorName: string;
    authorEmail?: string;
    authorInsta?: string;
    authorLinkedin?: string;
    numOfArticles?: number;
  };
  keyword: {
    id: string;
    nameKo: string;
    descKo: string;
    descEn: string;
    categoryId: string;
    categoryNameKo: string;
  };
  product?: {
    id: string;
    nameKo: string;
    nameEn?: string;
    imageUrl?: string;
    numOfArticles?: number;
  };
  images: ArticleImage[];
  studyLinks: string[];
  selectedCategory: string | undefined;
  uxEvaluationTag: string;
  dataSource: string;
}

interface ArticleFormCommonProps {
  formData: ArticleFormData;
  categories: Category[];
  availableKeywords: Keyword[];
  products: any[];
  authors: any[];
  uploadingImages: Set<number>;
  validationErrors?: Record<string, string>;
  updateFormData: (updates: Partial<ArticleFormData>) => void;
  handleCategoryChange: (categoryId: string) => void;
  handleProductChange: (productId: string) => void;
  handleKeywordChange: (keyword: Keyword) => void;
  handleAuthorChange: (author: any) => void;
  handleUxEvaluationChange: (uxEvaluation: string) => void;
  handleDataSourceChange: (dataSource: string) => void;
  addStudyLink: () => void;
  removeStudyLink: (index: number) => void;
  updateStudyLink: (index: number, value: string) => void;
  addImage: () => void;
  removeImage: (index: number) => void;
  updateImage: (
    index: number,
    field: keyof ArticleImage,
    value: string,
  ) => void;
  handleImageFileUpload: (index: number, file: File) => void;
  moveImageUp: (index: number) => void;
  moveImageDown: (index: number) => void;
}

const ArticleFormCommon = ({
  formData,
  categories,
  availableKeywords,
  products,
  authors,
  uploadingImages,
  validationErrors = {},
  updateFormData,
  handleCategoryChange,
  handleProductChange,
  handleKeywordChange,
  handleAuthorChange,
  handleUxEvaluationChange,
  handleDataSourceChange,
  addStudyLink,
  removeStudyLink,
  updateStudyLink,
  addImage,
  removeImage,
  updateImage,
  handleImageFileUpload,
  moveImageUp,
  moveImageDown,
}: ArticleFormCommonProps) => {
  return (
    <>
      <div className="form-section">
        <h2>기본 정보</h2>

        <div data-field="titleKo">
          <InputField
            label="제목 - 한국어"
            value={formData.titleKo}
            onChange={(value) => updateFormData({ titleKo: value })}
            placeholder="한국어 제목을 입력하세요"
            required
            error={validationErrors.titleKo}
          />
        </div>

        <InputField
          label="제목 - 영어"
          value={formData.titleEn}
          onChange={(value) => updateFormData({ titleEn: value })}
          placeholder="영어 제목을 입력하세요"
          required
        />
      </div>

      <div className="form-section">
        <h2>분류 정보</h2>

        <div data-field="product">
          <SelectField
            label="제품"
            value={
              formData.product
                ? {
                    label: formData.product.nameKo,
                    value: formData.product,
                  }
                : null
            }
            onChange={(option) => handleProductChange(option.value.id)}
            options={products.map((product) => ({
              label: product.nameKo,
              value: product,
            }))}
            placeholder="제품을 선택하세요"
            required
            error={validationErrors.product}
          />
        </div>

        <SelectField
          label="카테고리"
          value={
            formData.selectedCategory
              ? {
                  label:
                    categories.find((c) => c.id === formData.selectedCategory)
                      ?.nameKo || "",
                  value: categories.find(
                    (c) => c.id === formData.selectedCategory,
                  ),
                }
              : null
          }
          onChange={(option) => handleCategoryChange(option.value.id)}
          options={categories.map((category) => ({
            label: category.nameKo,
            value: category,
          }))}
          placeholder="카테고리를 선택하세요"
          required
        />

        <div data-field="keyword">
          <SelectField
            label="키워드"
            value={
              formData.keyword.id
                ? {
                    label: formData.keyword.nameKo || "",
                    value: availableKeywords.find(
                      (k) => k.id === formData.keyword.id,
                    ),
                  }
                : null
            }
            onChange={(option) => handleKeywordChange(option.value)}
            options={availableKeywords.map((keyword) => ({
              label: keyword.nameKo,
              value: keyword,
            }))}
            placeholder="키워드를 선택하세요"
            required
            error={validationErrors.keyword}
          />
        </div>

        <SelectField
          label="UX 평가 태그"
          value={
            formData.uxEvaluationTag
              ? {
                  label: formData.uxEvaluationTag,
                  value: formData.uxEvaluationTag,
                }
              : null
          }
          onChange={(option) => handleUxEvaluationChange(option.value)}
          options={Object.entries(UX_EVALUATION).map(([, value]) => ({
            label: value,
            value: value,
          }))}
          placeholder="UX 평가 태그를 선택하세요"
          required
        />

        <DataSourceToggle
          value={formData.dataSource}
          onChange={handleDataSourceChange}
        />
      </div>

      <div className="form-section">
        <h2>작성자 정보</h2>

        <SelectField
          label="작성자"
          value={
            formData.author.id
              ? {
                  label: formData.author.authorName,
                  value: authors.find((a) => a.id === formData.author.id),
                }
              : null
          }
          onChange={(option) => handleAuthorChange(option.value)}
          options={authors.map((author) => ({
            label: author.authorName,
            value: author,
          }))}
          placeholder="작성자를 선택하세요"
          required
        />
      </div>

      <div className="form-section">
        <h2>참고 링크</h2>
        <StudyLinksField
          links={formData.studyLinks}
          onAdd={addStudyLink}
          onRemove={removeStudyLink}
          onChange={updateStudyLink}
        />
      </div>

      <div className="form-section">
        <h2>본문</h2>
        <div data-field="contentKo">
          <ContentField
            contentKo={formData.contentKo}
            contentEn={formData.contentEn}
            onContentKoChange={(value) => updateFormData({ contentKo: value })}
            onContentEnChange={(value) => updateFormData({ contentEn: value })}
            required
            error={validationErrors.contentKo}
          />
        </div>
      </div>

      <div className="form-section">
        <h2>이미지</h2>
        <ImageField
          images={formData.images}
          onAdd={addImage}
          onRemove={removeImage}
          onChange={updateImage}
          onFileUpload={handleImageFileUpload}
          onMoveUp={moveImageUp}
          onMoveDown={moveImageDown}
          uploadingImages={uploadingImages}
        />
      </div>
    </>
  );
};

export default ArticleFormCommon;
